import { t } from "@lingui/macro";
import {
  RouteProp,
  useLinkTo,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef } from "react";
import { Platform } from "react-native";

import {
  isLocallySignedOutAtom,
  isUserAuthenticated_atom,
  preAuthenticatedPath_atom,
} from "../../features/Authentication/atoms";
import { AuthenticationLayout } from "../../features/Authentication/components/AuthenticationLayout";
import { AuthenticationModal } from "../../features/Authentication/components/AuthenticationModal/AuthenticationModal";
import { ExistingSessionRedirectLoader } from "../../features/Authentication/components/ExistingSessionRedirectLoader";
import { executePostLoginRedirect } from "../../features/Authentication/utils/executePostLoginRedirect";
import { resolvePostLoginRedirect } from "../../features/Authentication/utils/resolvePostLoginRedirect";
import { sessionAtom } from "../../features/Session";
import { BaseNavigatorParamList } from "../../navigators/BaseNavigator/types";
import { ClubStackNavigatorParamList } from "../../navigators/ClubNavigator";

export const ClubLoginScreen = () => {
  const [preAuthenticatedPath, setPreAuthenticatedPath] = useAtom(
    preAuthenticatedPath_atom
  );

  const appNavigation =
    useNavigation<NativeStackNavigationProp<BaseNavigatorParamList>>();
  const linkTo = useLinkTo();

  const setIsUserAuthenticated = useSetAtom(isUserAuthenticated_atom);
  const setIsLocallySignedOut = useSetAtom(isLocallySignedOutAtom);
  const isUserAuthenticated = useAtomValue(isUserAuthenticated_atom);
  const hasHandledExistingSession = useRef(false);
  const hasCompletedFreshLogin = useRef(false);

  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Login">>();

  const routeAfterLoginParam = route.params?.routeAfterLogin;
  const session = useAtomValue(sessionAtom);
  const defaultClubSlug =
    session?.user.clubs.find((club) => club.slug === route.params?.clubSlug)
      ?.slug ??
    session?.user.clubs[0]?.slug ??
    null;
  const existingSessionRedirectPath = resolvePostLoginRedirect({
    locationSearch: Platform.OS === "web" ? window.location.search : undefined,
    preAuthenticatedPath,
    routeAfterLoginParam,
  });

  useEffect(() => {
    if (
      !isUserAuthenticated &&
      routeAfterLoginParam &&
      routeAfterLoginParam !== preAuthenticatedPath
    ) {
      setPreAuthenticatedPath(routeAfterLoginParam);
    }
  }, [
    isUserAuthenticated,
    preAuthenticatedPath,
    routeAfterLoginParam,
    setPreAuthenticatedPath,
  ]);

  useEffect(() => {
    if (
      !session?.user ||
      hasCompletedFreshLogin.current ||
      hasHandledExistingSession.current
    ) {
      return;
    }

    const didRedirect = executePostLoginRedirect({
      clearPreAuthenticatedPath: () => setPreAuthenticatedPath(null),
      redirectExternal: (url) => {
        if (Platform.OS === "web") {
          window.location.href = url;
          return;
        }

        appNavigation.navigate("Club", {
          clubSlug: defaultClubSlug ?? "",
          screen: "Home",
          params: {
            screen: "WebView",
            params: {
              title: t`Continue`,
              url,
            },
          },
        });
      },
      redirectInApp: (path) => {
        linkTo(path);
      },
      redirectPath: existingSessionRedirectPath,
    });

    if (didRedirect) {
      hasHandledExistingSession.current = true;
      return;
    }

    if (!defaultClubSlug) {
      return;
    }

    hasHandledExistingSession.current = true;

    if (Platform.OS === "web") {
      window.location.href = `/patron/club/${defaultClubSlug}`;
      return;
    }

    appNavigation.navigate("Club", {
      clubSlug: defaultClubSlug,
      screen: "Home",
      params: { screen: "MainHome" },
    });
  }, [
    appNavigation,
    defaultClubSlug,
    existingSessionRedirectPath,
    linkTo,
    session?.user,
    setPreAuthenticatedPath,
  ]);

  const onComplete = () => {
    hasCompletedFreshLogin.current = true;
    setIsLocallySignedOut(false);
    setIsUserAuthenticated(true);

    const redirectPath = resolvePostLoginRedirect({
      locationSearch:
        Platform.OS === "web" ? window.location.search : undefined,
      preAuthenticatedPath,
      routeAfterLoginParam,
    });

    const didRedirect = executePostLoginRedirect({
      clearPreAuthenticatedPath: () => setPreAuthenticatedPath(null),
      redirectExternal: (url) => {
        if (Platform.OS === "web") {
          window.location.href = url;
          return;
        }

        appNavigation.navigate("Club", {
          clubSlug: route.params.clubSlug ?? "",
          screen: "Home",
          params: {
            screen: "WebView",
            params: {
              title: t`Continue`,
              url,
            },
          },
        });
      },
      redirectInApp: (path) => {
        linkTo(path);
      },
      redirectPath,
    });

    if (!didRedirect) {
      appNavigation.navigate("Club", {
        clubSlug: route.params.clubSlug ?? "",
        screen: "Home",
        params: { screen: "MainHome" },
      });
    }
  };

  if (session?.user && (defaultClubSlug || existingSessionRedirectPath)) {
    return <ExistingSessionRedirectLoader />;
  }

  return (
    <AuthenticationLayout>
      <AuthenticationModal
        isOpen={true}
        onComplete={onComplete}
        clubSlug={route.params.clubSlug ?? ""}
        hideCloseButton
      />
    </AuthenticationLayout>
  );
};
